import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTheme } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import {
  Box,
  Button,
  Container,
  Grid,
  MenuItem,
  TextField,
  Typography,
  Backdrop,
  CircularProgress,
} from '../../../../../components/mui/Core/index'
import PageTitle from '../../../../../components/PageTitle/page-title'
import Constants from '../../../../../constants/en.json'
import GenericSearch from '../../../../../components/GenericSearch/generic-search'
import PermissionsList from '../PermissionsList/PermissionsList'
import GenericInputCardRenderer from '../GenericInputCardRenderer/GenericInputCardRenderer'
import { useApplicationList } from '../../../../../hooks/applications/application-list'
import { fetchRoleApplication } from '../PermissionsList/permissions-list-slice'
import { createRole, updateRole } from './update-role-slice'
import { setSnackbar } from '../../../../../components/GenericSnackbar/generic-snackbar-slice'

function UpdateRole() {
  const theme = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const applicationList = useApplicationList()
  const [searchVal, setSearchVal] = useState('')
  const isEditingRole = location?.state?.isEditingRole
  const selectedRole = location?.state?.selectedRole
  const isFetchingPermissionsByApp = useSelector(
    (state) => state?.permissions?.isFetchingPermissionsByApp,
  )
  const isUpdatingRole = useSelector((state) => state.roleManagement.updateRole.isUpdatingRole)
  const roleApplicationList = useSelector((state) => state?.permissions?.roleApplication)
  const [roleCode, setRoleCode] = useState('')
  const [roleDescription, setRoleDescription] = useState('')
  const [roleApplication, setRoleApplication] = useState('')
  const [selectedPermissions, setSelectedPermissions] = useState([])

  const isLoading = isFetchingPermissionsByApp || isUpdatingRole
  const isDetailsFilled =
    roleCode?.trim()?.length > 0 &&
    roleDescription?.trim()?.length > 0 &&
    roleApplication?.trim()?.length > 0

  useEffect(() => {
    if (isEditingRole && selectedRole) {
      setRoleCode(selectedRole?.code)
      setRoleDescription(selectedRole?.description)
      setRoleApplication(selectedRole?.application)
    }
  }, [selectedRole, isEditingRole])

  useEffect(() => {
    dispatch(fetchRoleApplication())
  }, [dispatch])

  const handleExitCreateRole = () => {
    navigate('/role-management')
  }
  const handleSubmitRole = async () => {
    if (!isDetailsFilled) return
    const role = { roleName: roleCode, roleDescription, publicRole: true }
    try {
      if (isEditingRole) {
        await dispatch(
          updateRole({ role, application: roleApplication, permissions: selectedPermissions }),
        ).unwrap()

        dispatch(
          setSnackbar({
            message: Constants.roleManagement.editRole.SUCCESS_EDIT_ROLE.replace(
              '__ROLE_CODE__',
              roleCode,
            ),
            severity: 'success',
            action: true,
            autoHideDuration: 3000,
          }),
        )
      } else {
        await dispatch(
          createRole({ role, application: roleApplication, permissions: selectedPermissions }),
        ).unwrap()

        dispatch(
          setSnackbar({
            message: Constants.roleManagement.createRole.SUCCESS_CREATE_ROLE.replace(
              '__ROLE_CODE__',
              roleCode,
            ),
            severity: 'success',
            action: true,
            autoHideDuration: 3000,
          }),
        )
      }

      handleExitCreateRole()
    } catch (err) {
      dispatch(
        setSnackbar({
          message: Constants.roleManagement.createRole.ERROR_UPDATE_ROLE.replace(
            '__ROLE_CODE__',
            roleCode,
          ),
          severity: 'error',
          action: true,
          autoHideDuration: 3000,
        }),
      )
    }
  }
  const returnSearch = (inputValue) => {
    setSearchVal(inputValue)
  }

  const handleRoleCodeChange = (event) => setRoleCode(event.target.value)
  const handleRoleDescriptionChange = (event) => setRoleDescription(event.target.value)
  const handleRoleApplicationChange = (event) => {
    setRoleApplication(event.target.value)
    setSelectedPermissions([])
  }

  const textFieldStyle = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '4px',
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.secondary.main,
      },
    },
    margin: 0,
  }
  const selectStyle = {
    fontSize: '16px',
    color: theme.palette.common.black,
    borderColor: theme.palette.grey[700],
    justifyContent: 'flex-end',
    width: '100%',
    margin: '0',
  }
  const menuItemStyle = {
    width: '100%',
    padding: '10px 14px',
  }
  const permissionsContainer = {
    display: 'flex',
    flexDirection: 'row',
    gap: 4,
    justifyContent: 'space-between',
  }
  const headContainerStyle = { alignItems: 'center', marginBottom: '20px' }
  const headButtonsContainerStyle = { display: 'flex', justifyContent: 'flex-end', gap: 3 }
  const roleEditorStyle = { display: 'flex', flexDirection: 'column', gap: 4 }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Backdrop open={isLoading}>
        <CircularProgress color='inherit' />
      </Backdrop>
      <Container maxWidth='xl'>
        <Grid container spacing={2} sx={headContainerStyle}>
          <Grid size={{ xs: 8 }}>
            <PageTitle
              title={
                isEditingRole
                  ? Constants.roleManagement.editRole.EDIT_ROLE
                  : Constants.roleManagement.createRole.CREATE_ROLE
              }
            />
          </Grid>
          <Grid size={{ xs: 4 }} sx={headButtonsContainerStyle}>
            <Button variant='outlined' color='secondary' onClick={handleExitCreateRole}>
              {Constants.roleManagement.createRole.EXIT}
            </Button>
            <Button
              color='primary'
              variant='contained'
              onClick={handleSubmitRole}
              disabled={!isDetailsFilled}
            >
              {Constants.roleManagement.createRole.SUBMIT}
            </Button>
          </Grid>
        </Grid>

        <Grid container spacing={4} size={{ xs: 12 }}>
          <Grid size={{ xs: 4 }} sx={roleEditorStyle}>
            <GenericInputCardRenderer title={Constants.roleManagement.createRole.ROLE_CODE}>
              {isEditingRole && <Typography>{roleCode}</Typography>}
              {!isEditingRole && (
                <TextField
                  type='text'
                  fullWidth
                  sx={textFieldStyle}
                  value={roleCode}
                  label={Constants.roleManagement.createRole.ROLE_CODE}
                  placeholder={Constants.roleManagement.createRole.ROLE_CODE}
                  disabled={isEditingRole}
                  onChange={handleRoleCodeChange}
                />
              )}
            </GenericInputCardRenderer>
            <GenericInputCardRenderer title={Constants.roleManagement.createRole.ROLE_DESCRIPTION}>
              <TextField
                type='text'
                fullWidth
                sx={textFieldStyle}
                value={roleDescription}
                label={Constants.roleManagement.createRole.ROLE_DESCRIPTION}
                placeholder={Constants.roleManagement.createRole.ROLE_DESCRIPTION}
                onChange={handleRoleDescriptionChange}
              />
            </GenericInputCardRenderer>
            <GenericInputCardRenderer title={Constants.roleManagement.createRole.ROLE_APPLICATION}>
              {isEditingRole && (
                <Typography>{applicationList[selectedRole?.application]}</Typography>
              )}
              {!isEditingRole && (
                <TextField
                  select
                  fullWidth
                  sx={selectStyle}
                  value={roleApplication}
                  label={Constants.roleManagement.createRole.ROLE_APPLICATION}
                  disabled={isEditingRole}
                  onChange={handleRoleApplicationChange}
                >
                  {roleApplicationList?.map((option) => {
                    return (
                      <MenuItem sx={menuItemStyle} key={option.name} value={option.name}>
                        {option.description}
                      </MenuItem>
                    )
                  })}
                </TextField>
              )}
            </GenericInputCardRenderer>
          </Grid>

          <Grid size={{ xs: 8 }}>
            {roleApplication?.length > 0 ? (
              <>
                <Box sx={permissionsContainer}>
                  <Typography variant='cardHeader'>
                    {Constants.roleManagement.createRole.PERMISSIONS}
                  </Typography>
                  <GenericSearch returnSearch={returnSearch} />
                </Box>
                <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
                  <PermissionsList
                    searchvalue={searchVal}
                    roleApplication={roleApplication}
                    roleCode={roleCode}
                    isEditingRole={isEditingRole}
                    selectedPermissions={selectedPermissions}
                    setSelectedPermissions={setSelectedPermissions}
                  />
                </Grid>
              </>
            ) : (
              <Typography align='center' sx={{ mt: '50px' }}>
                {Constants.roleManagement.createRole.SELECT_APPLICATION_TO_VIEW_PERMISSIONS}
              </Typography>
            )}
          </Grid>
        </Grid>
      </Container>
    </Box>
  )
}

export default UpdateRole
